<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：ドラッグツリー
 *
 * 作成者：2021/11/22 ノア）黄
 * 最終修正者：2022/1/24 ノア）黄
 *
 -->
<template>
  <draggable
    class="dragArea"
    tag="ul"
    :list="data"
    :group="group"
    :move="checkMove"
    @change="onUpdate"
    v-bind="dragOptions"
  >
    <template v-if="isContextShow()">
      <div :class="{ 'last-node': el.isLastNode, 'root-node': el.level === '0' }" v-for="el in data" :key="el.id">
        <div class="node">
          <!-- 項目開閉 -->
          <span class="expander" v-if="el.children && el.children.length > 0" @click="() => onExpandIconClick(el)">
            <span v-if="el.expand"><tti-svg-icon icon-name="su_021"></tti-svg-icon></span>
            <span v-else><tti-svg-icon icon-name="su_022"></tti-svg-icon></span>
          </span>
          <span v-else class="expandSkeleton"></span>
          <!-- 項目アイコン -->
          <span v-if="el.src" class="expandSkeleton"><tti-svg-icon :icon-name="el.src"></tti-svg-icon></span>
          <span>{{ el.label }}</span>
        </div>
        <dragableTree
          v-if="el.children"
          :data="el.children"
          :canDropToRoot="canDropToRoot"
          :showContext="el.expand"
          :disabled="disabled"
          @treeUpdate="onUpdate"
          @disableDragCallback="onDisableDragCallback"
        />
      </div>
    </template>
  </draggable>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch, Emit } from 'vue-property-decorator'
import draggable, { MoveEvent } from 'vuedraggable'

import { TreeData, NoDragNode } from '@/models'

/**
 * ドラッグツリーコンポーネント
 *
 * @class
 */
@Component({
  name: 'dragableTree',
  components: {
    draggable
  }
})
export default class DraggableTree extends Vue {
  @Prop()
  data!: TreeData[]

  @Prop({
    default: 'treeMaster'
  })
  group: string | undefined

  @Prop({
    default: true
  })
  showContext: boolean | undefined

  @Prop({
    default: false
  })
  canDropToRoot: boolean | undefined

  @Prop({
    default: false
  })
  disabled: boolean | undefined

  dragOptions = {
    animation: 200,
    ghostClass: 'ghost'
  }

  @Watch('showContext', { immediate: true })
  isContextShow(): boolean {
    return this.showContext as boolean
  }

  @Emit('treeUpdate')
  treeUpdate(): void {
    return
  }

  @Emit('disableDragCallback')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  disableDragCallback(noDragNode: NoDragNode): any {
    return noDragNode
  }

  onExpandIconClick(el: TreeData): void {
    this.$log.debug('DraggableTree > onExpandIconClick, el:', el)
    el.expand = !el.expand
    this.$forceUpdate()
  }

  checkMove(evt: MoveEvent<TreeData>): boolean {
    this.$log.debug('DraggableTree > checkMove, evt:', evt)
    if (this.disabled) {
      return false
    }

    const { draggedContext, related, to } = evt
    if (draggedContext.element.disabled) {
      const noDragNode = { label: draggedContext.element.label, level: draggedContext.element.level || '' }
      this.disableDragCallback(noDragNode)
      return false
    }

    if (!this.canDropToRoot && related.classList.contains('root-node')) {
      return false
    }

    if (to && to.parentElement && to.parentElement.classList.contains('last-node')) {
      return false
    }

    return true
  }

  onUpdate(): void {
    this.treeUpdate()
  }

  onDisableDragCallback(noDragNode: NoDragNode): void {
    this.disableDragCallback(noDragNode)
  }
}
</script>

<style lang="scss" scoped>
.expander {
  margin-right: 4px;
  cursor: pointer;
}

.expandSkeleton {
  width: 28px;
  display: inline-block;
}

.ghost {
  .node {
    background: $C42 !important;
    color: $C01 !important;
  }
}
</style>
